import {
  Avatar,
  Badge,
  Body1Strong,
  Button,
  Caption1,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  mergeClasses,
  Tooltip,
  tokens,
} from "@fluentui/react-components";
import {
  MoreHorizontalRegular,
  SignOutRegular,
  WeatherMoonRegular,
  WeatherSunnyRegular,
} from "@fluentui/react-icons";
import type { ReactNode } from "react";
import { baeTypography } from "../theme/baeTheme";
import { DESKTOP, motion, PHONE, TAP_TARGET } from "../ui/tokens";

/** One destination in the navigation. */
export type NavItem<T extends string> = {
  value: T;
  label: string;
  icon: ReactNode;
  /** The permission that reveals it. The API is still the authority; this only avoids offering
   * a page that would be refused. */
  permission: string;
  /** Whether it belongs on a phone's bottom bar. Everything else moves into "More". */
  primary?: boolean;
};

/**
 * How many destinations fit across the bottom of a phone.
 *
 * Five including "More", so four real ones. A sixth makes each target narrower than a thumb and
 * turns the bar into a row of unreadable labels.
 */
const PHONE_SLOTS = 4;

const useStyles = makeStyles({
  shell: {
    display: "grid",
    minHeight: "100vh",
    minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground2,
    gridTemplateAreas: `"bar" "main" "tabs"`,
    gridTemplateRows: "auto 1fr auto",
    // The sidebar only appears where there is room to stand beside the content. Below that the
    // navigation goes to the bottom of the screen, where a thumb already is.
    [DESKTOP]: {
      gridTemplateAreas: `"side bar" "side main"`,
      gridTemplateColumns: "auto 1fr",
      gridTemplateRows: "auto 1fr",
    },
  },

  // ---- Sidebar, desktop only ----
  side: {
    gridArea: "side",
    display: "none",
    [DESKTOP]: {
      display: "flex",
      flexDirection: "column",
      gap: tokens.spacingVerticalXXS,
      width: "13.5rem",
      padding: tokens.spacingHorizontalM,
      backgroundColor: tokens.colorNeutralBackground1,
      borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
      position: "sticky",
      top: 0,
      alignSelf: "start",
      height: "100vh",
    },
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXS}`,
    marginBottom: tokens.spacingVerticalS,
  },
  mark: {
    display: "inline-grid",
    placeItems: "center",
    width: "28px",
    height: "28px",
    flexShrink: 0,
    borderRadius: tokens.borderRadiusMedium,
    // A gradient rather than a flat fill: it is the only place the electric blue appears at full
    // strength, and it is the one thing on screen that is purely identity.
    backgroundImage: `linear-gradient(135deg, ${tokens.colorBrandBackground} 0%, ${tokens.colorCompoundBrandStroke} 100%)`,
    color: "#FFFFFF",
    fontFamily: baeTypography.fontFamilyDisplay,
    fontSize: "14px",
    fontWeight: 700,
  },
  wordmark: {
    fontFamily: baeTypography.fontFamilyDisplay,
    fontSize: "15px",
    fontWeight: 700,
    letterSpacing: "-0.02em",
    margin: 0,
    whiteSpace: "nowrap",
  },
  navButton: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    width: "100%",
    minHeight: TAP_TARGET,
    padding: `0 ${tokens.spacingHorizontalS}`,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground2,
    font: "inherit",
    fontSize: tokens.fontSizeBase300,
    textAlign: "left",
    cursor: "pointer",
    transitionProperty: "background-color, color",
    transitionDuration: motion.fast,
    ":hover": { backgroundColor: tokens.colorNeutralBackground3 },
  },
  navOn: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorCompoundBrandForeground1,
    fontWeight: 600,
    ":hover": { backgroundColor: tokens.colorBrandBackground2Hover },
  },
  navIcon: { display: "grid", placeItems: "center", fontSize: "18px", flexShrink: 0 },
  spacer: { flexGrow: 1 },

  // ---- Top bar ----
  bar: {
    gridArea: "bar",
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    minWidth: 0,
    position: "sticky",
    top: 0,
    zIndex: 2,
    [PHONE]: { padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}` },
  },
  // The mark and wordmark live in the sidebar on desktop, so the bar only shows them where there
  // is no sidebar.
  barBrand: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
    [DESKTOP]: { display: "none" },
  },
  barTitle: {
    display: "none",
    [DESKTOP]: {
      display: "block",
      fontFamily: baeTypography.fontFamilyDisplay,
      letterSpacing: "-0.01em",
    },
  },
  who: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    lineHeight: 1.2,
    minWidth: 0,
  },
  whoText: { [PHONE]: { display: "none" } },

  main: {
    gridArea: "main",
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalXL}`,
    maxWidth: "82rem",
    width: "100%",
    margin: "0 auto",
    minWidth: 0,
    // The page must never be the thing that scrolls sideways. Anything genuinely too wide — a
    // label sheet — scrolls inside its own box instead.
    overflowX: "hidden",
    [PHONE]: {
      padding: tokens.spacingHorizontalM,
      // Clear of the bottom bar, plus the home-indicator area on an iPhone.
      paddingBottom: `calc(4.5rem + env(safe-area-inset-bottom, 0px))`,
    },
  },

  // ---- Bottom bar, phone and tablet ----
  tabs: {
    gridArea: "tabs",
    display: "flex",
    alignItems: "stretch",
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    position: "fixed",
    insetInline: 0,
    bottom: 0,
    zIndex: 3,
    paddingBottom: "env(safe-area-inset-bottom, 0px)",
    [DESKTOP]: { display: "none" },
  },
  tab: {
    flex: "1 1 0",
    display: "grid",
    justifyItems: "center",
    alignContent: "center",
    gap: "2px",
    minWidth: 0,
    minHeight: "3.5rem",
    padding: `${tokens.spacingVerticalXS} 2px`,
    border: "none",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    font: "inherit",
    cursor: "pointer",
    transitionProperty: "color",
    transitionDuration: motion.fast,
  },
  tabOn: { color: tokens.colorCompoundBrandForeground1 },
  tabIcon: { fontSize: "22px", lineHeight: 1 },
  tabLabel: {
    fontSize: "10px",
    lineHeight: 1.1,
    textAlign: "center",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  // A short bar above the active icon, so the current page is readable without relying on colour
  // alone.
  //
  // Centred explicitly. With no `left`, an absolutely positioned element falls at its static
  // position — the left edge of the wrapper — so this drew a stub hanging off the far left of the
  // bar rather than a marker over the icon.
  tabPip: {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "1.75rem",
    height: "3px",
    borderRadius: "0 0 3px 3px",
    backgroundColor: tokens.colorCompoundBrandStroke,
  },
  tabWrap: { position: "relative", flex: "1 1 0", display: "flex", minWidth: 0 },
});

/**
 * The frame every screen sits in.
 *
 * Two navigations, not one responsive compromise. On a laptop a sidebar shows every destination
 * at once and costs horizontal space nobody misses; on a phone the same list was a horizontally
 * scrolling tab strip, where the destinations past the edge were never found. A bottom bar puts
 * four of them under the thumb that is already there and the rest one tap away.
 */
export function AppShell<T extends string>({
  items,
  current,
  onNavigate,
  tenantLabel,
  isPlatform,
  displayName,
  email,
  isDark,
  onToggleTheme,
  onSignOut,
  children,
}: {
  items: NavItem<T>[];
  current: T | null;
  onNavigate: (value: T) => void;
  tenantLabel: string;
  isPlatform: boolean;
  displayName: string;
  email: string;
  isDark: boolean;
  onToggleTheme: () => void;
  onSignOut: () => void;
  children: ReactNode;
}) {
  const styles = useStyles();

  // The first four keep their own slot; everything else shares "More". Marking an item `primary`
  // pulls it forward, so "Add a piece" stays a thumb away even as settings screens pile up.
  const ranked = [...items].sort((a, b) => Number(b.primary ?? false) - Number(a.primary ?? false));
  const onBar = ranked.slice(0, PHONE_SLOTS);
  const inMore = ranked.slice(PHONE_SLOTS);
  const activeIsInMore = inMore.some((item) => item.value === current);
  const activeLabel = items.find((item) => item.value === current)?.label;

  return (
    <div className={styles.shell}>
      <nav className={`${styles.side} no-print`} aria-label="Sections">
        <div className={styles.brand}>
          <span className={styles.mark}>B</span>
          <h1 className={styles.wordmark}>CommerceOS</h1>
        </div>

        {items.map((item) => (
          <button
            key={item.value}
            type="button"
            aria-current={current === item.value ? "page" : undefined}
            className={mergeClasses(styles.navButton, current === item.value && styles.navOn)}
            onClick={() => onNavigate(item.value)}
          >
            <span className={styles.navIcon}>{item.icon}</span>
            {item.label}
          </button>
        ))}

        <div className={styles.spacer} />

        <Badge appearance="tint" color={isPlatform ? "informative" : "brand"} size="medium">
          {tenantLabel}
        </Badge>
      </nav>

      <header className={`${styles.bar} no-print`}>
        <div className={styles.barBrand}>
          <span className={styles.mark}>B</span>
          <Badge appearance="tint" color={isPlatform ? "informative" : "brand"} size="small">
            {tenantLabel}
          </Badge>
        </div>

        <Body1Strong className={styles.barTitle}>{activeLabel}</Body1Strong>

        <div className={styles.spacer} />

        <div className={styles.who}>
          <span className={styles.whoText}>
            <Body1Strong>{displayName}</Body1Strong>
          </span>
          <span className={styles.whoText}>
            <Caption1>{email}</Caption1>
          </span>
        </div>

        <Tooltip content={isDark ? "Switch to light" : "Switch to dark"} relationship="label">
          <Button
            appearance="subtle"
            icon={isDark ? <WeatherSunnyRegular /> : <WeatherMoonRegular />}
            onClick={onToggleTheme}
          />
        </Tooltip>

        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="subtle"
              icon={<Avatar name={displayName} size={24} color="brand" />}
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem icon={<SignOutRegular />} onClick={onSignOut}>
                Sign out
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </header>

      <main className={styles.main}>{children}</main>

      <nav className={`${styles.tabs} no-print`} aria-label="Sections">
        {onBar.map((item) => (
          <span key={item.value} className={styles.tabWrap}>
            {current === item.value && <span className={styles.tabPip} />}
            <button
              type="button"
              aria-current={current === item.value ? "page" : undefined}
              className={mergeClasses(styles.tab, current === item.value && styles.tabOn)}
              onClick={() => onNavigate(item.value)}
            >
              <span className={styles.tabIcon}>{item.icon}</span>
              <span className={styles.tabLabel}>{item.label}</span>
            </button>
          </span>
        ))}

        {inMore.length > 0 && (
          <span className={styles.tabWrap}>
            {activeIsInMore && <span className={styles.tabPip} />}
            <Menu positioning="above-end">
              <MenuTrigger disableButtonEnhancement>
                <button
                  type="button"
                  className={mergeClasses(styles.tab, activeIsInMore && styles.tabOn)}
                >
                  <span className={styles.tabIcon}>
                    <MoreHorizontalRegular />
                  </span>
                  <span className={styles.tabLabel}>More</span>
                </button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  {inMore.map((item) => (
                    <MenuItem
                      key={item.value}
                      // Wrapped, because Fluent's icon slot takes an element rather than any
                      // ReactNode and a bare node is rejected by its slot type.
                      icon={<span className={styles.navIcon}>{item.icon}</span>}
                      onClick={() => onNavigate(item.value)}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </MenuPopover>
            </Menu>
          </span>
        )}
      </nav>
    </div>
  );
}
